import http from '@/plugins/axios';

const settingsMapper = ({
  shopName,
  notificationEmail,
  fromEmail,
  shopOwnerName,
  reviewRequestEmail,
  reviewPublishing,
  sendReviewRequests,
  sendReviewRequestsOmnisend,
  sendReviewRequestsKlaviyo,
  sendPhotoRequests,
  sendPhotoRequestWaitDays,
  photoRequestStarSubset,
  postPurchaseEmailDays,
  postPurchaseEmailDaysInternational,
  postPurchaseEmailDaysPosOrder,
  resendReviewRequests,
  resendEmailWaitDays,
  postPurchaseEmailSendAtHour,
  postPurchaseEmailSendAtMinute,
  blacklistedReviewRequestEmails,
  sendOneReviewRequestPerOrder,
  useProductVariantNameInRr,
  importsAutomaticSync,
  reportsFrequency,
  hideAllOpinewWidgets,
  customCss,
  autoReplaceObfuscatedReviewerNames,
  importedReviewAutoApprove,
  importerProfiles,
  emailDomain,
  emailSubdomain,
  emailRecords,
  showReviewsFromArchivedProducts,
  dontSendToRepeatCustomers,
  dashboardLanguage,
  advancedReviewRequestsEmailTemplateActive,
  advancedCouponEmailTemplateActive,
  advancedUnverifiedReviewTemplateActive,
  advancedPhotoRequestsEmailTemplateActive,
  requestFormStarsColor,
  requestFormImageUrl,
  requestFormCustomCss,
  qAndAActive,
  requestSequenceAdvancedView,
  sendOnlyReviewRequestsSms,
  reviewRequestExcludedProducts,
  deactivateEmailNotifications,
  emailTranslationEnabled,
  logoUrl,
}) => {
  return {
    general: {
      dashboardLanguage,
      shopName,
      notificationEmail,
      fromEmail,
      shopOwnerName,
      reviewRequestEmail,
      hideAllOpinewWidgets,
      customCss,
      showReviewsFromArchivedProducts,
      qAndAActive,
      deactivateEmailNotifications,
      emailTranslationEnabled,
      logoUrl,
    },
    reviews: {
      reviewPublishing,
    },
    reviewRequests: {
      sendReviewRequests,
      sendReviewRequestsKlaviyo,
      sendPhotoRequests,
      sendPhotoRequestWaitDays,
      photoRequestStarSubset,
      dontSendToRepeatCustomers,
      postPurchaseEmailDays,
      postPurchaseEmailDaysInternational,
      postPurchaseEmailDaysPosOrder,
      resendReviewRequests,
      resendEmailWaitDays,
      postPurchaseEmailSendAtHour,
      postPurchaseEmailSendAtMinute,
      blacklistedReviewRequestEmails,
      sendOneReviewRequestPerOrder,
      useProductVariantNameInRr,
      emailDomain,
      emailSubdomain,
      emailRecords,
      sendReviewRequestsOmnisend,
      advancedReviewRequestsEmailTemplateActive,
      advancedCouponEmailTemplateActive,
      advancedUnverifiedReviewTemplateActive,
      advancedPhotoRequestsEmailTemplateActive,
      requestFormStarsColor,
      requestFormImageUrl,
      requestFormCustomCss,
      requestSequenceAdvancedView,
      sendOnlyReviewRequestsSms,
      reviewRequestExcludedProducts,
    },
    reviewImporting: {
      importsAutomaticSync: importsAutomaticSync || false,
      autoReplaceObfuscatedReviewerNames: autoReplaceObfuscatedReviewerNames,
      importedReviewAutoApprove: importedReviewAutoApprove,
      importerProfiles: importerProfiles,
    },
    other: {
      reportsFrequency,
    },
  };
};

const payloadMapper = ({ general, reviews, reviewRequests, reviewImporting, other }) => ({
  ...general,
  ...reviews,
  ...reviewRequests,
  ...reviewImporting,
  ...other,
});

const BASE_URL = '/settings';

export default {
  getSettings: () => {
    return http
      .get(BASE_URL)
      .then(response => response.data)
      .then(settingsMapper);
  },
  updateSettings: (data, arg = null) => {
    const parsedUrl = !arg ? BASE_URL : BASE_URL + '?rrc=1';
    return http
      .post(parsedUrl, payloadMapper(data))
      .then(response => response.data.data)
      .then(settingsMapper);
  },
  updateVersion: version => {
    return http.post(`settings/dashboard_version/${version}`).then(response => response.data);
  },
  sendSignatureEmail: data => {
    return http.post('/add-postmark-signature', data).then(response => response.data);
  },
  verifySignatureEmail: () => {
    return http.get('/validate-postmark-signature').then(response => response.data);
  },
  getThemes: () => {
    return http.get('/settings/themes').then(response => response.data);
  },
  getWidgets: themeId => {
    return http.get(`/settings/themes/${themeId}/widgets`).then(response => response.data);
  },
  installWidget: (themeId, data) => {
    return http
      .post(`/settings/themes/${themeId}/install-widget`, data)
      .then(response => response.data);
  },
  uninstallWidget: (themeId, data) => {
    return http
      .post(`/settings/themes/${themeId}/uninstall-widget`, data)
      .then(response => response.data);
  },
  installAllWidgets: (themeId, data) => {
    return http
      .post(`/settings/themes/${themeId}/install-all-widgets`, data)
      .then(response => response.data);
  },
  uninstallAllWidgets: themeId => {
    return http
      .post(`/settings/themes/${themeId}/uninstall-all-widgets`)
      .then(response => response.data);
  },
  syncShopifyContacts: () => {
    return http.get('/settings/push-to-shopify-contacts');
  },
  openPermissionWindow() {
    return http
      .get('/settings/update-scopes')
      .then(res => res.data)
      .then(res => {
        window.top.location = res.data.confirmationRedirectUrl;
      })
      .catch(err => {
        return err;
      });
  },
  updateAsset: (themeId, data) => {
    return http
      .post(`/settings/themes/${themeId}/update-asset`, data)
      .then(response => response.data);
  },
  checkStatus: themeId => {
    return http.get(`/settings/themes/${themeId}/check-status`).then(response => response.data);
  },
  getAssets: themeId => {
    return http.get(`/settings/themes/${themeId}/get-assets`).then(response => response.data);
  },
  getAssetCode: (themeId, assetKey) => {
    return http
      .get(`/settings/themes/${themeId}/asset?key=${assetKey}`)
      .then(response => response.data);
  },
  validateEmailRecords: () => {
    return http.post('/settings/validate-email-records').then(response => response.data);
  },
};
